<template>
  <ProSpaceGeoLayout :routeName="$route.name" isParent>
    <template #top>
      <ProSpaceTopPanel :header="this.$t('competenceDirectories')">
        <template #right>
          <ProSpaceIconButton
            icon="update"
            iconWidth="16px"
            iconHeight="16px"
            containerSize="20px"
            @click="actionUpdate"
          />
          <ProSpaceDots
            :options="dotsHeaderOptions"
            :isSquare="true"
            @action="(action) => handleHeaderDots(action)"
          />
          <ProSpaceButton
            v-if="checkAccess('add-button')"
            ref="add-button"
            type="primary"
            icon="plus"
            :label="this.$t('competenceDirectoryCreateNew')"
            @click="
              $router.push({
                path: `/competence/competencedirectory`,
                query: {
                  block: this.selectedNode ? this.selectedNode.code : null,
                },
              })
            "
          />
        </template>
      </ProSpaceTopPanel>
    </template>
    <template #left>
      <ProSpaceLeftPanel
        class="basetype-tree__left-panel"
        :panelName="$t('competenceDirectoryLeftTreeHeaderTitle')"
        searchable
        :searchPlaceholder="$t('searchTxt')"
        @search="searchTree"
      >
        <template #center>
          <ProSpaceTree
            ref="tree"
            :searchPlaceholder="$t('searchTxt')"
            :service="baseTypeService"
            searchName="name"
            getNodeMethod="treeWithAllWithoutIcon"
            propKey="id"
            propLabel="name"
            :default-filter="getDefaultFilter()"
            :prop-parent-id="null"
            @select="nodeSelect"
          />
        </template>
      </ProSpaceLeftPanel>
    </template>
    <template #center>
      <ProSpaceGeoLayout>
        <template #top>
          <ProSpaceFilterPanel
            :closeTxt="$t('closeTxt')"
            :searchPlaceholder="$t('searchTxt')"
            :txtResetFilters="$t('txtResetFilters')"
            :modelFastFilter="modelFastFilter"
            :fastFilters="fastFilters"
            :unicKeyStorage="competenceDirectoryService.constructor.name + 'grid'"
            @apply="submitFilter"
            ref="filterPanel"
            filter-model="name"
            @update:modelFastFilter="
              (val) => {
                modelFastFilter = val;
                selectedMultipleClear();
              }
            "
            :ignore-clear-keys="['block']"
          >
            <template #hint>
              {{ this.$t("filterBy") }}
            </template>
            <template #filters="{ filter }">
              <ProSpaceMultiSelect
                :localization="$t"
                v-model="filter.level"
                :service="baseTypeService"
                getMethod="grid"
                optionLabel="name"
                optionValue="code"
                :default-filter="{
                  disabled: false,
                  systemDirectoryCode: 'LevelType',
                }"
                :placeholderValue="$t('competenceDirectoryLevel')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearFilterTxt')"
                @apply="emitFilter"
              />

              <ProSpaceMultiSelect
                :localization="$t"
                v-model="filter.type"
                :service="baseTypeService"
                getMethod="grid"
                optionLabel="name"
                optionValue="code"
                :default-filter="{
                  disabled: false,
                  systemDirectoryCode: 'CompetenceType',
                }"
                :placeholderValue="$t('competenceDirectoryType')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearFilterTxt')"
                @apply="emitFilter"
              />

              <ProSpaceCustomFilterPanel
                :customTxt="$t('custom')"
                :customFilterTxt="$t('customFilterTxt')"
                :selectAllTxt="$t('selectAllTxt')"
                :closeTxt="$t('closeTxt')"
                :applyTxt="$t('applyTxt')"
                :clearFilterTxt="$t('clearFilterTxt')"
                :fromTxt="$t('fromTxt')"
                :toTxt="$t('toTxt')"
                :services="customServices"
                :unicKeyStorage="competenceDirectoryService.constructor.name + 'grid'"
                @apply="(model) => customFilterApply(filter, model)"
              />
            </template>
          </ProSpaceFilterPanel>
        </template>
        <template #center>
          <ProSpaceMainGrid
            ref="grid"
            getMethod="grid"
            :service="competenceDirectoryService"
            :noMountedRequest="true"
            defaultColumnWidths="300"
            @selected-change="selectedHandler"
            @db-click="toItem"
          >
            <template #columns>
              <Column
                field="name"
                :header="this.$t('competenceDirectoryName')"
                sortable
                headerClass="move-sort-head"
                frozen
                class="column-table-header-cell"
              >
                <template #body="field">
                  <ProSpaceTableHeaderCell
                    v-if="field.data"
                    :id="field.data.id"
                    :status="getStatus(field.data.status)"
                    :title="field.data.name"
                    @click-on-title="toItem(field.data)"
                  />
                </template>
              </Column>

              <Column
                field="levelName"
                :header="this.$t('competenceDirectoryLevel')"
                sortable
                headerClass="move-sort-head"
              >
                <template #body="field">
                  <ProSpaceDefaultTextColumn>
                    {{ field.data.levelName }}
                  </ProSpaceDefaultTextColumn>
                </template>
              </Column>

              <Column
                field="typeName"
                :header="this.$t('competenceDirectoryType')"
                sortable
                headerClass="move-sort-head"
              >
                <template #body="field">
                  <ProSpaceDefaultTextColumn>
                    {{ $t(field.data.typeName) }}
                  </ProSpaceDefaultTextColumn>
                </template>
              </Column>

              <Column
                field="actions"
                class="column-end column-end-30"
                frozen
                alignFrozen="right"
              >
                <template #body="field">
                  <ProSpaceDots
                    :options="getDots()"
                    @action="(action) => handleDots(action, field.data)"
                  ></ProSpaceDots>
                </template>
              </Column>
            </template>
            <template #empty>
              <ProSpaceEmptyGrid
                icon="error-message"
                :title="$t('noDataTitle')"
                :text="$t('noDataTxt')"
              />
            </template>
          </ProSpaceMainGrid>
        </template>
      </ProSpaceGeoLayout>

      <CompetenceDirectoryModalImport
        v-model="showImportModal"
        @loading="loadingHandler"
        accept=".csv, .xlsx"
      />
      <ProSpaceHistoryModal
        ref="history"
        v-model="showHistoryModal"
        modelName="CompetenceDirectoryApiModel"
        @close="closeHistory"
      />
    </template>
    <template #right>
      <CompetenceDirectoryPanel
        v-if="hasSelect"
        ref="right-panel"
        :service="competenceDirectoryService"
        :options="getDots()"
        @clear="selectedMultipleClear"
        @edit="editItem"
        @delete="deleteItem"
        @history="showHistory"
      />
    </template>
  </ProSpaceGeoLayout>
  <ProSpaceFlashMessage
    v-if="showDeleteModal"
    v-model="showDeleteModal"
    type="default"
    icon="flash-warning"
    :title="$t('deletingConfirmation')"
    :applyTxt="$t('confirmTxt')"
    :cancelTxt="$t('reject')"
    @apply="applyDelete"
  >
    <ProSpaceVLayout :gap="10" justify-content="center" align-items="center">
      <div
        class="text-color-gray text-bold"
        style="padding: 0 5px"
        v-html="$t('deleteCompetenceDirectory', { name: deletingModel.name })"
      ></div>
    </ProSpaceVLayout>
  </ProSpaceFlashMessage>
</template>
<script>
import {
  ProSpaceFilterPanel,
  ProSpaceCustomFilterPanel,
  ProSpaceMultiSelect,
  ProSpaceMainGrid,
  ProSpaceDots,
  ProSpaceModal,
  ProSpaceGeoLayout,
  ProSpaceTopPanel,
  ProSpaceIconButton,
  ProSpaceButton,
  ProSpaceTableHeaderCell,
  ProSpaceDateColumn,
  ProSpaceDefaultTextColumn,
  ProSpaceEmptyGrid,
  ProSpaceLeftPanel,
  ProSpaceTree,
  ProSpaceVLayout,
  ProSpaceFilterContainsChips,
  ProSpaceFilterDatesRange,
  ProSpaceDropdown,
  ProSpaceInputCalendar,
  ProSpaceInputNumber,
  ProSpaceInputSwitch,
  ProSpaceInputText,
  ProSpaceFlashMessage,
} from "@prospace/prospace-components-library";
import accessMixin from "../../../../../Frontends/Common/utils/elementAccess/accessMixin";
import { CompetenceDirectoryService } from "../../services/CompetenceDirectoryService.js";

import Column from "primevue/column";
import CompetenceDirectoryPanel from "./components/CompetenceDirectoryPanel";
import ProSpaceHistoryModal from "../../../../../Frontends/ProSpace/components/modals/ProSpaceHistoryModal";
import CompetenceDirectoryModalImport from "./components/CompetenceDirectoryModalImport";
const _ = require("lodash");
import { mapGetters } from "vuex";
import { ref } from "vue";
import { useMultiSelectGrid } from "@composes/multiselect";
import { BaseTypeService } from "../../../../BaseType/BaseTypeService.Frontend/services/BaseTypeService";
export default {
  mixins: [accessMixin],
  components: {
    ProSpaceFilterPanel,
    ProSpaceCustomFilterPanel,
    ProSpaceMultiSelect,
    ProSpaceMainGrid,
    ProSpaceDots,
    ProSpaceModal,
    Column,
    ProSpaceGeoLayout,
    ProSpaceTopPanel,
    ProSpaceIconButton,
    ProSpaceButton,
    ProSpaceTableHeaderCell,
    ProSpaceDateColumn,
    ProSpaceDefaultTextColumn,
    ProSpaceEmptyGrid,
    CompetenceDirectoryPanel,
    ProSpaceHistoryModal,
    ProSpaceLeftPanel,
    ProSpaceTree,
    ProSpaceVLayout,
    ProSpaceFilterContainsChips,
    ProSpaceFilterDatesRange,
    ProSpaceDropdown,
    ProSpaceInputCalendar,
    ProSpaceInputNumber,
    ProSpaceInputSwitch,
    ProSpaceInputText,
    CompetenceDirectoryModalImport,
    ProSpaceFlashMessage,
  },
  setup() {
    const grid = ref(null);
    const filterPanel = ref(null);
    const {
      selectedMultipleGridHandler,
      selectedMultipleClear,
      hasSelect,
    } = useMultiSelectGrid(grid, filterPanel);
    return {
      filterPanel,
      grid,
      selectedMultipleGridHandler,
      selectedMultipleClear,
      hasSelect,
    };
  },
  data() {
    return {
      showImportModal: false,
      showHistoryModal: false,

      gridDefaultFilter: {},

      // Service
      competenceDirectoryService: new CompetenceDirectoryService(),
      baseTypeService: new BaseTypeService(),

      //InlinePanel
      masterId: null,
      isEdit: false,
      isCreate: false,
      modalModel: {},

      // Multi filter
      modelFastFilter: {
        label: this.$t("allTxt"),
        field: "disabled",
        value: false,
      },

      deletingModel: null,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      userinfo: "userinfoStore/getUserinfo",
      access: "securityRoleAccess/access",
    }),
    customServices() {
      const filters = [
        {
          type: "chips",
          model: "description",
          label: this.$t("competenceDirectoryDescription"),
          selected: {},
        },
        {
          type: "chips",
          model: "advancementRecommendation",
          label: this.$t("competenceDirectoryAdvancementRecommendation"),
          selected: {},
        },
      ];
      //if (this.checkAccess("metainfo-by-block"))
      filters.push(
        {
          type: "chips",
          model: "createdBy",
          label: this.$t("createdByTxt"),
          selected: {},
        },
        {
          type: "chips",
          model: "updatedBy",
          label: this.$t("updatedByTxt"),
          selected: {},
        }
      );

      filters.push(
        {
          type: "dates-range-time",
          model: "createdAt",
          label: this.$t("createDate"),
          optionValue: "date",
          selected: {},
          dateFormat: this.userinfo.dateFormat,
        },
        {
          type: "dates-range-time",
          model: "lastUpdated",
          label: this.$t("lastUpdatedTxt"),
          optionValue: "date",
          selected: {},
          dateFormat: this.userinfo.dateFormat,
        }
      );
      return filters;
    },
    dotsHeaderOptions() {
      return [
        {
          name: "export_xlsx",
          text: this.$t("export_xlsx"),
          icon: "upload",
          liteRef: "export-xlsx-option",
        },
      ];
    },
    // dotsOptions() {
    //   let res = [
    //     {
    //       name: "showHistory",
    //       text: this.$t("history"),
    //       icon: "history",
    //       method: this.showHistory,
    //     },
    //   ];
    //   if (checkAccess("edit-option")) {
    //     res.push({
    //       name: "edit",
    //       text: this.$t("editItemTxt"),
    //       icon: "edit",
    //       method: this.editItem,
    //       liteRef: "edit-option",
    //     });
    //   }
    //   if (checkAccess("delete-option")) {
    //     res.push({
    //       name: "delete",
    //       text: this.$t("delete"),
    //       icon: "delete",
    //       method: this.deleteItem,
    //       liteRef: "delete-option",
    //     });
    //   }
    //   if (this.modelFastFilter.label === this.$t("deleted")) {
    //     return res.filter((option) => option.name !== "edit" && option.name !== "delete");
    //   }

    //   return res;
    // },
    fastFilters() {
      return [
        { label: this.$t("allTxt"), field: "disabled", value: false },
        { label: this.$t("deleted"), field: "disabled", value: true },
      ];
    },
  },
  methods: {
    getDots(){
      let res = [
        {
          name: "showHistory",
          text: this.$t("history"),
          icon: "history",
          method: this.showHistory,
        },
      ];
      if (this.checkAccess("edit-option")) {
        res.push({
          name: "edit",
          text: this.$t("editItemTxt"),
          icon: "edit",
          method: this.editItem,
          liteRef: "edit-option",
        });
      }
      if (this.checkAccess("delete-option")) {
        res.push({
          name: "delete",
          text: this.$t("delete"),
          icon: "delete",
          method: this.deleteItem,
          liteRef: "delete-option",
        });
      }
      if (this.modelFastFilter.label === this.$t("deleted")) {
        return res.filter((option) => option.name !== "edit" && option.name !== "delete");
      }

      return res;
    },

    searchTree(search) {
      this.$refs.tree.handlerSearch(search);
    },
    getDefaultFilter() {
      return {
        disabled: { and: [false] },
        systemDirectoryCode: { and: ["BlockType"] },
      };
    },
    closeHistory() {
      this.showHistoryModal = false;
    },
    // checkAccess(elem, model) {
    //   return this.checkRoleAccess(elem, model, this.access);
    // },
    showHistory(record) {
      this.$refs.history.tabs[0].masterId = record.id;
      this.$refs.history.masterName = record.name;
      this.$nextTick(() => {
        this.showHistoryModal = true;
      });
    },
    toItem(data) {
      if (!data || data.disabled) return;
      this.$router.push(`/competence/competencedirectory/${data.id}`);
    },
    submitFilter({ filter, search }) {
      this.grid.submitFilter(filter, search);
    },
    emitFilter() {
      this.filterPanel.submitFilter();
    },
    customFilterApply(filter, model) {
      filter = Object.assign(filter, model);
      this.filterPanel.submitFilter();
    },
    actionUpdate() {
      this.grid.getRecords(true);
      if (this.$refs["right-panel"]) {
        this.$refs["right-panel"].update();
      }
    },
    actionExport(format) {
      this.loadingHandler(true);
      this.competenceDirectoryService
        .export(this.grid.getRecords(false, true), format)
        .then(() => this.loadingHandler(false));
      //TODO: Catch error?
      //TODO: Show message(export task created)
    },
    actionImport() {
      this.showImportModal = true;
    },
    loadingHandler(val) {
      this.grid.loading = val;
    },
    selectedHandler(val) {
      this.selectedMultipleGridHandler(val);
    },
    editItem(model) {
      if (!model) return;
      this.toItem(model);
    },
    changeItem() {
      let method;
      if (this.isCreate) {
        method = "create";
      }
      if (this.isEdit) {
        method = "update";
      }
      this.competenceDirectoryService[method](this.modalModel).then(() => {
        this.actionUpdate();
      });
    },
    deleteItem(model) {
      if (!model) return;
      this.deletingModel = model;
      this.showDeleteModal = true;
    },
    applyDelete() {
      this.competenceDirectoryService.delete(this.deletingModel).then(() => {
        this.actionUpdate();
        this.selectedMultipleClear();
        this.deletingModel = null;
      });
    },
    getStatus(status) {
      if (!status) return;
      let type = "default";
      return {
        text: status[0].toUpperCase() + status.toLowerCase().slice(1),
        type,
      };
    },
    handleDots(action, item) {
      var option = this.getDots().find((o) => o.name === action);
      option.method(item);
    },
    handleHeaderDots(action) {
      if (action === "export_xlsx") {
        this.actionExport("XLSX");
      }
    },
    nodeSelect(value) {
      if (this.selectedNode && this.selectedNode.key === value.key) return;
      var filter = value.id === 0 ? { and: [] } : { and: [value.code] };
      this.filterPanel.filter["block"] = filter;
      this.filterPanel.submitFilter();
      this.selectedNode = _.cloneDeep(value);
    },
  },
};
</script>
<style lang="scss">
@import "../../assets/scss/main.scss";
.basetype-tree {
  .radio-tabs {
    margin-top: 1px;
    .radio-tab:nth-child(2) {
      display: none !important;
    }
    &-body {
      margin: 10px;
    }
  }

  &__left-panel {
    border: 1px solid var(--prospace-ui-border-color);
  }
}
.prospace-multiselect {
  &-panel {
    .p-multiselect-items-wrapper {
      min-height: 120px;
    }
  }
}
</style>
